import React, { useEffect } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchAstrositeStates,
  selectAstrositesStatesById,
  selectAstrositeStatesByContext,
} from "../astrosites/slice"
import { useInterval } from "../timing/use-interval"
const UPDATE_INTERVAL = 30000 // ms

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

/**
 * The Disk usage for an Astrosite rendered as a bar chart
 * @param dataset
 * @param usePercent
 * @returns {false|JSX.Element}
 * @constructor
 */
const AstroSiteDiskUsage = ({ astrositeID }) => {
  const dispatch = useDispatch()

  const astrositeStatesById = useSelector(selectAstrositesStatesById)

  const updateData = () => {
    // We want to make sure we get results for the astrosite
    dispatch(
      fetchAstrositeStates({
        filter: {
          astrosite_id: astrositeID,
        },
        page: {
          offset: 0,
          limit: 1,
        },
        context: `du/astrosite/${astrositeID}`,
      })
    )
  }

  useEffect(() => {
    updateData()
  }, [])

  // Poll at an interval
  useInterval(updateData, UPDATE_INTERVAL, false)

  const stateIDs = useSelector((state) =>
    selectAstrositeStatesByContext(state, `du/astrosite/${astrositeID}`)
  )

  const datasets = transformData(astrositeStatesById[stateIDs?.ids[0]])

  function transformData(input) {
    if (!input) {
      return []
    }
    const result = []
    const backgroundColorMapping = {
      Control: "rgba(120, 206, 240, 0.5)",
      Telemetry: "rgba(235, 174, 89, 0.5)",
      NAS: "rgb(235, 226, 103, 0.5)",
      Gateway: "rgb(240, 127, 166, 0.5)",
    }

    // Function to get or create label object
    function getOrCreateLabel(label) {
      let labelObj = result.find((obj) => obj.label === label)
      if (!labelObj) {
        labelObj = {
          label,
          data: [],
          backgroundColor:
            backgroundColorMapping[label] || "rgb(201, 203, 207)", // Default color if not predefined
        }
        result.push(labelObj)
      }
      return labelObj
    }

    // Iterate through each item and its computers to populate the result
    input?.computers?.forEach((c, i) => {
      const labelObj = getOrCreateLabel(c.name)
      labelObj?.data.push(
        Math.floor(
          (Number(c.used_space) /
            (Number(c.used_space) + Number(c.available_space))) *
            100
        )
      )
      labelObj.stack = `Stack ${i}`
      labelObj.astrosite = input.astrosite
      labelObj.data.push(c.val)
    })

    return result
  }

  function removeDuplicates(objects) {
    const seenNames = new Set()
    return objects.filter((obj) => {
      if (seenNames.has(obj.astrosite)) {
        return false // Skip this object, it's a duplicate
      } else {
        seenNames.add(obj.astrosite)
        return true // Keep this object, it's not a duplicate
      }
    })
  }

  const labels = removeDuplicates(datasets).map((ds) => ds.astrosite)

  const data = {
    labels,
    datasets: datasets,
  }

  const options = {
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          beginAtZero: true,
          callback: function (value) {
            return value + "%"
          },
        },
      },
    },
    responsive: true,
    plugins: {
      legend: {
        position: "right",
      },
      title: {
        display: true,
        text: `${astrositeID} disk usage`,
      },
    },
  }

  return <Bar options={options} data={data} />
}

AstroSiteDiskUsage.propTypes = {
  dataset: PropTypes.object,
  usePercent: PropTypes.bool,
}

AstroSiteDiskUsage.propTypes = {
  astrositeID: PropTypes.string, // the ID of the Astrosite for which we want disk usage
}

export default AstroSiteDiskUsage
